/**
 * Google reCAPTCHA v3 for the auth forms (login and registration).
 *
 * v3 rather than the "I'm not a robot" checkbox: it scores silently in the
 * background so the user never sees a challenge on the way into the app.
 *
 * The token this produces is only meaningful because the API verifies it
 * server-side (see RecaptchaUtil.verify_recaptcha) — a browser-side check on
 * its own stops nothing.
 *
 * Set REACT_APP_RECAPTCHA_SITE_KEY to switch it on; with it unset nothing loads
 * and the forms behave exactly as they do today. The matching secret goes on
 * the API as RECAPTCHA_SECRET_KEY (already used by the consultation form).
 */
export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

export const recaptchaEnabled = Boolean(RECAPTCHA_SITE_KEY);

const SCRIPT_ID = "recaptcha-v3";

/** Loaded once per page, shared by every form on it. */
let scriptPromise = null;

function loadScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error("reCAPTCHA script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Warm the script up ahead of submission so the first submit isn't slowed by a
 * cold network fetch. Safe to call on mount; a no-op when not configured.
 */
export function preloadRecaptcha() {
  if (!recaptchaEnabled) return;
  loadScript().catch(() => {
    /* Reported at submit time, where it can actually be acted on. */
  });
}

/**
 * A token for one submission, or `null` if reCAPTCHA could not run at all
 * (script blocked, offline). Callers decide what to do with `null` — the forms
 * surface it rather than submitting something the API will reject.
 *
 * @param {string} action labels the submission in the reCAPTCHA console and is
 *   checked server-side, so use a distinct one per form ("login", "register").
 * @returns {Promise<string|null>}
 */
export async function getRecaptchaToken(action) {
  if (!recaptchaEnabled) return null;
  try {
    await loadScript();
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;

    return await new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then(resolve)
          .catch(() => resolve(null));
      });
    });
  } catch {
    return null;
  }
}
